import React, { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import BankFeedPanel, {
  isSameVaultTransfer,
  formatDate,
  money,
} from '@/components/integrations/bank-feed/BankFeedPanel'
import { useVaultOptions } from '@/components/integrations/mapping/VaultMappingRow'
import NeedsInputList from '@/components/integrations/bank-feed/NeedsInputList'
import useGetIncomeCategories from '@/services/api/transaction-category/useGetIncomeCategories'
import useGetExpenseCategories from '@/services/api/transaction-category/useGetExpenseCategories'
import { useXeroMapping } from '@/services/api/integrations/useXeroMapping'
import {
  accountingProvider,
  providerLabel,
  useAccountingIntegrationFeature,
} from '@/services/api/integrations/accountingProviders'
import {
  type FeedFilter,
  type FeedRow,
  useBankFeed,
  useBulkFeedAction,
  useConfirmHighConfidence,
} from '@/services/api/integrations/useBankFeed'

const FILTERS: { value: FeedFilter; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Matched' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'all', label: 'All' },
]

function statusBadge(row: FeedRow, provider: string) {
  const {
    status,
    confidence_score: score,
    provider_changed_at: changed,
  } = row.match
  if (changed) return <Badge variant="destructive">Changed in {provider}</Badge>
  if (row.raw_transaction.is_foreign_currency)
    return <Badge variant="outline">{row.raw_transaction.currency_code}</Badge>
  if (isSameVaultTransfer(row))
    return <Badge variant="outline">Same-vault transfer</Badge>
  if (
    status === 'actual_only' &&
    row.raw_transaction.type.endsWith('-TRANSFER')
  )
    return (
      <Badge variant="secondary">
        {row.raw_transaction.paired_raw_transaction_id
          ? 'Transfer'
          : 'Transfer — unpaired'}
      </Badge>
    )
  switch (status) {
    case 'suggested':
      return <Badge variant="secondary">Suggested {score}%</Badge>
    case 'confirmed':
    case 'manual':
      return <Badge>Reconciled</Badge>
    case 'excluded':
      return <Badge variant="outline">Excluded</Badge>
    case 'rejected':
      return <Badge variant="outline">Rejected</Badge>
    default:
      return <Badge variant="outline">No match</Badge>
  }
}

/** The "Unreconciled" fallback categories — a line in one still needs a mapping. */
function useUnreconciledCategoryIds() {
  const income = useGetIncomeCategories()
  const expense = useGetExpenseCategories()
  return useMemo(() => {
    const ids = new Set<number>()
    for (const item of [...(income.data ?? []), ...(expense.data ?? [])]) {
      for (const category of item.is_group
        ? item.transaction_categories ?? []
        : [item]) {
        if (category.system_key) ids.add(category.id)
      }
    }
    return ids
  }, [income.data, expense.data])
}

export default function BankFeedPage() {
  const navigate = useNavigate()
  const { hasFeature, loaded: featuresLoaded } =
    useAccountingIntegrationFeature()

  return (
    <div className="mx-auto min-h-screen max-w-7xl px-4 pb-10 pt-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-txt-secondary">Bank Feed</h3>
          <p className="text-sm text-txt-secondary">
            Reconcile bank transactions imported from your accounting software
          </p>
        </div>
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </Button>
      </div>
      {!featuresLoaded ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !hasFeature ? (
        <Card className="p-6 text-sm">
          Accounting integrations are not included in this Space's plan.
        </Card>
      ) : (
        <FeedBody />
      )}
    </div>
  )
}

function FeedBody() {
  const [params, setParams] = useSearchParams()
  const filter = (params.get('filter') as FeedFilter) || 'pending'
  const vaultId = params.get('vault') ? Number(params.get('vault')) : null
  const { data, isPending, isError } = useBankFeed(filter, vaultId)
  const { data: mapping } = useXeroMapping()
  const vaults = useVaultOptions()
  const unreconciledCategoryIds = useUnreconciledCategoryIds()
  const bulk = useBulkFeedAction()
  const highConfidence = useConfirmHighConfidence()
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [openId, setOpenId] = useState<number | null>(null)

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
    setSelected(new Set())
  }

  if (isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>
  if (isError || !data)
    return (
      <Card className="p-6 text-sm">
        Could not load the Bank Feed. Please try again.
      </Card>
    )
  if (mapping && !mapping.connection) {
    return (
      <Card className="space-y-3 p-6 text-sm">
        <p>No accounting software is connected to this Space.</p>
        <Link to="/account?section=integrations">
          <Button variant="outline">Go to Integrations</Button>
        </Link>
      </Card>
    )
  }

  const rows = data.data
  const openRow = rows.find((r) => r.raw_transaction.id === openId) ?? null
  const canReconcile = data.can.reconcile
  const vaultName = (id: number | null) =>
    id === null
      ? 'Unmapped bank account'
      : vaults.find((v) => v.id === id)?.name ?? `Vault #${id}`
  const accountName = (id: number | null) =>
    mapping?.accounts.find((a) => a.id === id)?.name
  const selectedRows = rows.filter((r) => selected.has(r.raw_transaction.id))
  const confirmable = selectedRows.filter(
    (r) => r.match.status === 'suggested' && r.match.pf_transaction_id,
  )
  const toggle = (id: number, on: boolean) => {
    const next = new Set(selected)
    if (on) next.add(id)
    else next.delete(id)
    setSelected(next)
  }
  const runBulk = async (action: 'confirm' | 'exclude', ids: number[]) => {
    await bulk.mutateAsync({ action, rawIds: ids })
    setSelected(new Set())
  }

  return (
    <div className="space-y-4">
      <NeedsInputList
        invoices={data.needs_input ?? []}
        canEdit={canReconcile}
      />

      {/* Banking overview: lines waiting per vault; click to filter. */}
      {data.pending_by_vault.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.pending_by_vault.map((v) => (
            <button
              key={v.vault_id ?? 'none'}
              type="button"
              onClick={() =>
                setParam(
                  'vault',
                  vaultId === v.vault_id || v.vault_id === null
                    ? null
                    : String(v.vault_id),
                )
              }
              className={cn(
                'flex items-center gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm hover:bg-layout-secondary',
                vaultId !== null &&
                  vaultId === v.vault_id &&
                  'border-primary ring-1 ring-primary',
              )}
            >
              {v.vault_id === null && (
                <AlertTriangle className="size-4 text-amber-600" />
              )}
              <span>
                <span className="block font-medium">
                  {vaultName(v.vault_id)}
                </span>
                {v.last_transaction_date && (
                  <span className="block text-xs text-muted-foreground">
                    Latest line {formatDate(v.last_transaction_date)}
                  </span>
                )}
              </span>
              <span className="rounded-full bg-red-600 px-1.5 text-xs font-semibold text-white">
                {v.pending}
              </span>
            </button>
          ))}
          {vaultId !== null && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setParam('vault', null)}
            >
              Show all vaults
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Tabs
          value={filter}
          onValueChange={(value) =>
            setParam('filter', value === 'pending' ? null : value)
          }
        >
          <TabsList className="flex-wrap">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
                {f.value === 'pending' && data.pending_count > 0 && (
                  <span className="ml-1.5 rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                    {data.pending_count}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap gap-2">
          {canReconcile && selected.size > 0 ? (
            <>
              <span className="self-center text-sm text-muted-foreground">
                {selected.size} selected
              </span>
              <Button
                size="md"
                disabled={confirmable.length === 0 || bulk.isPending}
                onClick={() =>
                  runBulk(
                    'confirm',
                    confirmable.map((r) => r.raw_transaction.id),
                  )
                }
              >
                Confirm matched ({confirmable.length})
              </Button>
              <Button
                variant="outline"
                size="md"
                disabled={bulk.isPending}
                onClick={() => runBulk('exclude', [...selected])}
              >
                Exclude selected
              </Button>
            </>
          ) : (
            canReconcile && (
              <Button
                variant="outline"
                size="md"
                onClick={() => highConfidence.mutate(undefined)}
                disabled={highConfidence.isPending}
              >
                Confirm all high-confidence
              </Button>
            )
          )}
          <Link
            to={`/integrations/${accountingProvider(mapping?.connection?.provider)?.slug ?? 'xero'}/mapping`}
          >
            <Button variant="ghost" size="md">
              Account mapping
            </Button>
          </Link>
        </div>
      </div>

      <div
        className={cn(
          'grid gap-0',
          openRow && 'lg:grid-cols-[minmax(0,1fr)_420px]',
        )}
      >
        <Card className="min-w-0 overflow-x-auto p-0">
          {rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              {filter === 'pending'
                ? 'Nothing to review — you are all caught up.'
                : 'No bank lines here.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  {canReconcile && (
                    <th className="w-8 p-3">
                      <Checkbox
                        aria-label="Select all"
                        checked={selected.size === rows.length}
                        onCheckedChange={(on) =>
                          setSelected(
                            on === true
                              ? new Set(rows.map((r) => r.raw_transaction.id))
                              : new Set(),
                          )
                        }
                      />
                    </th>
                  )}
                  <th className="p-3">Date</th>
                  <th className="p-3">Description</th>
                  <th className="p-3">Account / Vault</th>
                  <th className="p-3 text-right">Deposits</th>
                  <th className="p-3 text-right">Withdrawals</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const raw = row.raw_transaction
                  return (
                    <tr
                      key={raw.id}
                      onClick={() => setOpenId(raw.id)}
                      className={cn(
                        'cursor-pointer border-b last:border-b-0 hover:bg-layout-secondary',
                        openId === raw.id && 'bg-accent-tr-08',
                      )}
                    >
                      {canReconcile && (
                        <td
                          className="p-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            aria-label={`Select line ${raw.description ?? raw.id}`}
                            checked={selected.has(raw.id)}
                            onCheckedChange={(on) =>
                              toggle(raw.id, on === true)
                            }
                          />
                        </td>
                      )}
                      <td className="whitespace-nowrap p-3">
                        {formatDate(raw.transaction_date)}
                      </td>
                      <td className="max-w-xs p-3">
                        <span className="line-clamp-2 break-words">
                          {raw.description || '—'}
                        </span>
                        {raw.reference && (
                          <span className="block text-xs text-muted-foreground">
                            Ref: {raw.reference}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="block text-xs text-muted-foreground">
                          {accountName(raw.external_account_id)}
                        </span>
                        <span
                          className={cn(
                            raw.resolved_vault_id === null && 'text-amber-700',
                          )}
                        >
                          {vaultName(raw.resolved_vault_id)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap p-3 text-right text-green-700">
                        {raw.direction === 'in' ? money(raw.amount) : ''}
                      </td>
                      <td className="whitespace-nowrap p-3 text-right text-red-700">
                        {raw.direction === 'out' ? money(raw.amount) : ''}
                      </td>
                      <td className="p-3">
                        {statusBadge(
                          row,
                          providerLabel(mapping?.connection?.provider),
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </Card>

        {openRow && (
          // Side panel on wide screens; full-screen sheet on phones and tablets.
          <div className="fixed inset-0 z-40 bg-white lg:static lg:z-auto lg:max-h-[calc(100vh-8rem)] lg:min-h-[480px]">
            <BankFeedPanel
              key={openRow.raw_transaction.id}
              row={openRow}
              mapping={mapping}
              canReconcile={canReconcile}
              unreconciledCategoryIds={unreconciledCategoryIds}
              onClose={() => setOpenId(null)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
